import React, { useEffect, useState } from 'react';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import { trashOutline } from 'ionicons/icons';
import { getStorageInfo, formatBytes } from '../services/storage/storage-monitor.service';
import { runCleanup } from '../services/storage/cache-cleanup.service';

const QUOTA_WARNING_THRESHOLD = 0.85;
const MIN_AVAILABLE_MB = 50;

const bannerStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  color: '#fff',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 14,
};

export const StorageWarning: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const check = async () => {
      setChecking(true);
      try {
        const info = await getStorageInfo();
        if (info.quotaBytes != null && info.usageBytes != null) {
          const usageRatio = info.usageBytes / info.quotaBytes;
          if (usageRatio >= QUOTA_WARNING_THRESHOLD) {
            setMessage(
              `Spazio quasi esaurito (${formatBytes(info.usageBytes)} / ${formatBytes(info.quotaBytes)}).`
            );
            setVisible(true);
            return;
          }
        }
        if (info.availableBytes != null && info.availableBytes < MIN_AVAILABLE_MB * 1024 * 1024) {
          setMessage(
            `Spazio disponibile basso (${formatBytes(info.availableBytes)}).`
          );
          setVisible(true);
          return;
        }
        setVisible(false);
      } catch {
        setVisible(false);
      } finally {
        setChecking(false);
      }
    };

    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, []);

  const handleCleanup = async () => {
    setLoading(true);
    try {
      const result = await runCleanup(50 * 1024 * 1024);
      if (result.bytesFreed > 0) {
        setMessage(`Liberati ${formatBytes(result.bytesFreed)}.`);
        setTimeout(() => setVisible(false), 3000);
      } else {
        setMessage('Nessuna cache da eliminare.');
      }
    } catch {
      setMessage('Errore durante la pulizia.');
    } finally {
      setLoading(false);
    }
  };

  if (checking || !visible) return null;

  return (
    <div style={bannerStyle} role="banner">
      <span>{message}</span>
      <IonButton fill="clear" color="light" onClick={handleCleanup} disabled={loading}>
        {loading ? <IonSpinner name="crescent" /> : <IonIcon icon={trashOutline} />}
        Libera spazio
      </IonButton>
    </div>
  );
};
